import { useEffect, useMemo, useCallback } from 'react'
import useSWRInfinite from 'swr/infinite'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import {
  SegmentListItemType,
  SegmentSortOrderType,
  SegmentCreationStatusType
} from 'src/lib/types/segment'
import { getSegmentSortQueryParam } from 'src/lib/utils/segmentsHelper'
import { parseApiTimestamp } from 'src/lib/utils/helpers'
import { errorToast } from 'src/lib/utils/toasters'

const SEGMENTS_MANAGER_LIST_KEY = 'segments-manager-list' as const

export type SegmentsManagerListSwrKey = readonly [
  typeof SEGMENTS_MANAGER_LIST_KEY,
  string,
  number,
  number,
  string,
  SegmentSortOrderType,
  boolean
]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

type SegmentListApiRow = {
  id?: unknown
  name?: unknown
  description?: unknown
  created_at?: unknown
  updated_at?: unknown
  active?: unknown
  type?: unknown
  status?: unknown
}

function parseSegmentListApiRow(raw: unknown): SegmentListApiRow | null {
  if (!isRecord(raw)) return null
  return raw as SegmentListApiRow
}

function mapSegmentApiRowToListItem(row: SegmentListApiRow): SegmentListItemType {
  const id = typeof row.id === 'string' ? row.id : ''
  const name = typeof row.name === 'string' ? row.name : ''
  const description = typeof row.description === 'string' ? row.description : ''
  const apiType = typeof row.type === 'string' ? row.type : ''
  const createdAt = parseApiTimestamp(row.created_at) ?? new Date(0)
  const updatedAt = parseApiTimestamp(row.updated_at) ?? new Date(0)
  const isEnabled = Boolean(row.active)

  const statusRaw = row.status
  const status = (
    typeof statusRaw === 'string' && statusRaw.length > 0 ? statusRaw : 'COMPLETED'
  ) as SegmentCreationStatusType

  return {
    id,
    name,
    description,
    createdAt,
    updatedAt,
    isEnabled,
    type: (apiType || 'PHONE_NUMBER') as SegmentListItemType['type'],
    constructType:
      apiType === 'SORTMENT_AUDIENCE' ? 'DYNAMIC' : apiType === 'CUSTOMER_TAG' ? 'CUSTOMER_TAG' : 'STATIC',
    status
  }
}

function normalizeSegmentListResponse(
  raw: unknown,
  searchParam: string
): { segmentList: SegmentListItemType[]; totalRowCount: number } {
  if (!isRecord(raw)) {
    return { segmentList: [], totalRowCount: 0 }
  }
  if ('error' in raw && raw.error) {
    const msg = typeof raw.error === 'string' ? raw.error : 'Request failed'
    errorToast(msg)
    const headers = raw.headers
    if (isRecord(headers) && typeof headers['x-trace-id'] === 'string') {
      console.error('x-trace-id:', headers['x-trace-id'])
    }
    return { segmentList: [], totalRowCount: 0 }
  }

  const envelope = raw['data']
  if (!isRecord(envelope)) {
    return { segmentList: [], totalRowCount: 0 }
  }

  const rowsUnknown = envelope['data']
  const totalUnknown = envelope['total_count']
  const totalRowCount = typeof totalUnknown === 'number' ? totalUnknown : 0

  if (!Array.isArray(rowsUnknown)) {
    return { segmentList: [], totalRowCount }
  }

  const searchLower = searchParam.trim().toLowerCase()

  const segmentList = rowsUnknown
    .map(parseSegmentListApiRow)
    .filter((row): row is SegmentListApiRow => row !== null)
    .filter(row => {
      if (!searchLower) return true
      const n = row.name
      return typeof n === 'string' && n.toLowerCase().includes(searchLower)
    })
    .map(mapSegmentApiRowToListItem)
    .sort((a, b) => {
      const enabledDiff = Number(b.isEnabled) - Number(a.isEnabled)
      if (enabledDiff !== 0) return enabledDiff
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    })

  return { segmentList, totalRowCount }
}

async function fetchSegmentsManagerList(
  key: SegmentsManagerListSwrKey
): Promise<{ segmentList: SegmentListItemType[]; totalRowCount: number }> {
  const [, accountId, limit, pageIndex, searchParam, sortOrder, hasDynamicSegmentsPermission] = key
  const url = `/merchant/${accountId}/segments?offset=${pageIndex + 1}&limit=${limit}${
    hasDynamicSegmentsPermission
      ? '&type=SORTMENT_AUDIENCE,PHONE_NUMBER,PINCODE,CUSTOMER_TAG'
      : '&type=PHONE_NUMBER,PINCODE,CUSTOMER_TAG'
  }${searchParam ? `&search=${encodeURIComponent(searchParam)}` : ''}${getSegmentSortQueryParam(sortOrder)}`

  const raw: unknown = await fetcher(url, apiURI.SEGMENTS_ENGINE)
  return normalizeSegmentListResponse(raw, searchParam)
}

const PAGE_SIZE = 10

export function useSegmentsManagerList(options: {
  accountId: string | undefined
  hasDynamicSegmentsPermission: boolean
  sortOrder: SegmentSortOrderType
  searchText: string
  /** i18n `t` from the parent page — avoids a second `useTranslation` inside this hook */
  t: (key: string) => string
}): {
  segmentList: SegmentListItemType[]
  totalRowCount: number
  loading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  loadMore: () => void
  callGetSegments: () => Promise<unknown>
} {
  const { accountId, hasDynamicSegmentsPermission, sortOrder, searchText, t } = options

  const getKey = useCallback(
    (pageIndex: number): SegmentsManagerListSwrKey | null => {
      if (!accountId) return null
      return [
        SEGMENTS_MANAGER_LIST_KEY,
        accountId,
        PAGE_SIZE,
        pageIndex,
        searchText,
        sortOrder,
        hasDynamicSegmentsPermission
      ] as const
    },
    [accountId, searchText, sortOrder, hasDynamicSegmentsPermission]
  )

  const { data: pages, setSize, isValidating, mutate } = useSWRInfinite(getKey, fetchSegmentsManagerList, {
    ...staticOptions,
    onError: () => errorToast(t('something_went_wrong'))
  })

  useEffect(() => {
    setSize(1)
  }, [searchText, sortOrder, setSize])

  const segmentList = useMemo(() => pages?.flatMap(page => page?.segmentList ?? []) ?? [], [pages])
  const totalRowCount = pages?.[0]?.totalRowCount ?? 0

  const lastPageCount = pages?.[pages.length - 1]?.segmentList?.length ?? 0
  const hasMore = segmentList.length < totalRowCount && lastPageCount === PAGE_SIZE

  const callGetSegments = useCallback(() => mutate(), [mutate])
  const loadMore = useCallback(() => setSize(current => current + 1), [setSize])

  return {
    segmentList,
    totalRowCount,
    loading: !accountId || (isValidating && segmentList.length === 0),
    isLoadingMore: isValidating && segmentList.length > 0,
    hasMore,
    loadMore,
    callGetSegments
  }
}
